"use client";

import { useEffect, useState } from "react";
import { RotateCcw, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  deleteHistory,
  listHistory,
  PlanGateError,
  type HistoryItem,
} from "@/lib/retrieval";

/** Recent lookups, newest first, each re-runnable with one click. */
export function LookupHistory({
  onRerun,
  refreshKey = 0,
}: {
  onRerun?: (address: string, topic: string) => void;
  /** Bump to refetch — the dashboard does this when a lookup finishes, so a
      fresh lookup appears without a page reload. */
  refreshKey?: number;
}) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [gated, setGated] = useState(false);
  const [loading, setLoading] = useState(true);

  async function handleRemove(item: HistoryItem) {
    // Optimistic: the row disappears at once; a failed delete puts it back.
    setItems((prev) => prev.filter((i) => i.id !== item.id));
    try {
      await deleteHistory(item.id);
    } catch {
      setItems((prev) =>
        [...prev, item].sort((a, b) => b.id - a.id),
      );
      showToast("Could not remove the lookup.");
    }
  }

  // Signed-out visitors get no fetch and no panel — the authenticated call
  // with no token guaranteed a 401 on every anonymous load (2026-08-29).
  useEffect(() => {
    if (!user) return;
    void (async () => {
      try {
        setItems(await listHistory());
      } catch (err) {
        if (err instanceof PlanGateError) setGated(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshKey, user]);

  if (!user || gated) return null;

  return (
    <div className="panel p-6">
      <p className="overline">Recent lookups</p>
      {loading ? (
        <p className="mt-3 text-body-sm text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-body-sm text-muted">
          Your lookups will appear here.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {items.slice(0, 10).map((item) => (
            <li key={item.id} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-body-sm text-ink">{item.address}</p>
                <p className="cite text-muted">
                  {item.topic}
                  {item.created_at
                    ? ` · ${new Date(item.created_at).toLocaleDateString()}`
                    : ""}
                </p>
              </div>
              {onRerun ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRerun(item.address, item.topic)}
                  title="Run again"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void handleRemove(item)}
                title="Remove from history"
                aria-label={`Remove lookup ${item.address} — ${item.topic}`}
                className="text-muted hover:text-ink"
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
